import { CatalogReferencesPage } from './catalog/CatalogReferencesPage';
import { GenerationPage } from './deliveryMap/generations/GenerationPage';
import { DeliveryZoneLiveMapPage } from './deliveryMap/DeliveryZoneLiveMapPage';
import { FileContextListPage } from './fileContexts/FileContextListPage';
import { FileContextEditorPage } from './fileContexts/FileContextEditorPage';
import { AdminOrderDetailPage } from './orders/AdminOrderDetailPage';
import { CatalogProductDetailPage } from './catalog/CatalogProductDetailPage';
import { CatalogCategoryDetailPage } from './catalog/CatalogCategoryDetailPage';
import { CatalogQueryProvider } from './catalog/CatalogQueryProvider';
import { CatalogProductEditorPage } from './catalog/CatalogProductEditorPage';
import { CatalogCategoryEditorPage } from './catalog/CatalogCategoryEditorPage';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { AdminBootstrapDictionaries, AuthToken } from '../../api/types';
import { AdminProvider, useAdminContext } from './AdminContext';
import { AdminLayout } from './AdminLayout';
import { ADMIN_COMPANIES_PATH, ADMIN_HOME_PATH, normalizeCatalogTab, normalizeCompanyDetailTab } from './adminRoutes';
import { AdminTheme } from './theme';
import { AdminHomePage } from './pages/AdminHomePage';
import { FileRegistryPage, QueueDashboardPage } from './pages/AdminDiagnosticsPages';
import {
  CatalogPage,
  CompaniesPage,
  CompanyDeliveryFormMode,
  CompanyDetailPage,
  CompanyDetailTab,
  DeliveryZonesPage,
  PriceImportDetailPage,
  PriceImportsPage,
  UserDetailPage,
  UsersPage,
} from './pages/AdminPages';

interface AdminWorkspaceProps {
  authToken: AuthToken;
  dictionaries: AdminBootstrapDictionaries;
  onLogout: () => void;
  onThemeToggle: () => void;
  theme: AdminTheme;
}

export function AdminWorkspace({
  authToken,
  dictionaries,
  onLogout,
  onThemeToggle,
  theme,
}: AdminWorkspaceProps) {
  return (
    <AdminProvider
      authToken={authToken}
      dictionaries={dictionaries}
      onLogout={onLogout}
      onThemeToggle={onThemeToggle}
      theme={theme}
    >
      <CatalogQueryProvider>
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminHomePage />} />
          <Route path="users" element={<UsersRoute />} />
          <Route path="users/:userId" element={<UserDetailRoute />} />
          <Route path="companies" element={<CompaniesRoute />} />
          <Route path="companies/:companyId" element={<CompanyDetailRoute />} />
          <Route path="companies/:companyId/:tab" element={<CompanyDetailRoute />} />
          <Route path="orders" element={<AdminOrderDetailPage />} />
          <Route path="orders/:orderId" element={<AdminOrderDetailPage />} />
          <Route path="companies/:companyId/delivery-addresses/new" element={<CompanyDeliveryAddressRoute mode="create" />} />
          <Route
            path="companies/:companyId/delivery-addresses/:addressId/edit"
            element={<CompanyDeliveryAddressRoute mode="edit" />}
          />
          <Route path="companies/:companyId/delivery-zones/new" element={<CompanyDeliveryZoneRoute mode="create" />} />
          <Route
            path="companies/:companyId/delivery-zones/:groupId/edit"
            element={<CompanyDeliveryZoneRoute mode="edit" />}
          />
          <Route path="delivery-zones" element={<DeliveryZoneListRoute />} />
          <Route path="delivery-zones/generations" element={<GenerationPage />} />
          <Route path="delivery-zones/live-map" element={<DeliveryZoneLiveMapPage />} />
          <Route path="delivery-zones/new" element={<DeliveryZoneRoute mode="create" />} />
          <Route path="delivery-zones/:zoneId/edit" element={<DeliveryZoneRoute mode="edit" />} />
          <Route path="catalog/products/new" element={<CatalogProductEditorPage />} />
          <Route path="catalog/products/:productId/edit" element={<CatalogProductEditorPage />} />
          <Route path="catalog/categories/new" element={<CatalogCategoryEditorPage />} />
          <Route path="catalog/categories/:categoryId/edit" element={<CatalogCategoryEditorPage />} />
          <Route path="catalog/references/:kind" element={<CatalogReferencesPage />} />
          <Route path="catalog" element={<CatalogRoute />} />
          <Route path="catalog/:tab" element={<CatalogRoute />} />
          <Route path="catalog/products/:productId" element={<CatalogProductDetailPage />} />
          <Route path="catalog/categories/:categoryId" element={<CatalogCategoryDetailPage />} />
          <Route path="imports" element={<PriceImportsRoute />} />
          <Route path="imports/:importId" element={<PriceImportDetailRoute />} />
          <Route path="file-contexts" element={<FileContextListPage />} />
          <Route path="file-contexts/new" element={<FileContextEditorPage />} />
          <Route path="file-contexts/:contextId/edit" element={<FileContextEditorPage />} />
          <Route path="diagnostics/files" element={<FileRegistryPage />} />
          <Route path="diagnostics/queues" element={<QueueDashboardPage />} />
          <Route path="*" element={<Navigate replace to={ADMIN_HOME_PATH} />} />
        </Route>
      </Routes>
      </CatalogQueryProvider>
    </AdminProvider>
  );
}

function UsersRoute() {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();

  return <UsersPage dictionaries={dictionaries} onNavigate={navigate} />;
}

function UserDetailRoute() {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const userId = requireRouteParam(params.userId, ADMIN_COMPANIES_PATH);

  if (userId === null) {
    return <Navigate replace to={ADMIN_COMPANIES_PATH} />;
  }

  return <UserDetailPage dictionaries={dictionaries} entityId={userId} onNavigate={navigate} />;
}

function CompaniesRoute() {
  const { dictionaries } = useAdminContext();

  return <CompaniesPage dictionaries={dictionaries} />;
}

function CompanyDetailRoute() {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const companyId = requireRouteParam(params.companyId, ADMIN_COMPANIES_PATH);

  if (companyId === null) {
    return <Navigate replace to={ADMIN_COMPANIES_PATH} />;
  }

  const activeTab = normalizeCompanyDetailTab(params.tab) as CompanyDetailTab;

  return (
    <CompanyDetailPage
      activeTab={activeTab}
      dictionaries={dictionaries}
      entityId={companyId}
      onNavigate={navigate}
    />
  );
}

function CompanyDeliveryAddressRoute({ mode }: { mode: CompanyDeliveryFormMode }) {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const companyId = requireRouteParam(params.companyId, ADMIN_COMPANIES_PATH);

  if (companyId === null) {
    return <Navigate replace to={ADMIN_COMPANIES_PATH} />;
  }

  return (
    <CompanyDetailPage
      activeTab="delivery-addresses"
      deliveryAddressFormAddressId={params.addressId}
      deliveryAddressFormMode={mode}
      dictionaries={dictionaries}
      entityId={companyId}
      onNavigate={navigate}
    />
  );
}

function CompanyDeliveryZoneRoute({ mode }: { mode: CompanyDeliveryFormMode }) {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const companyId = requireRouteParam(params.companyId, ADMIN_COMPANIES_PATH);

  if (companyId === null) {
    return <Navigate replace to={ADMIN_COMPANIES_PATH} />;
  }

  return (
    <CompanyDetailPage
      activeTab="delivery-zones"
      deliveryZoneFormGroupId={params.groupId}
      deliveryZoneFormMode={mode}
      dictionaries={dictionaries}
      entityId={companyId}
      onNavigate={navigate}
    />
  );
}

function CatalogRoute() {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const activeTab = normalizeCatalogTab(params.tab);

  return <CatalogPage activeTab={activeTab} dictionaries={dictionaries} onNavigate={navigate} />;
}

function DeliveryZoneRoute({ mode }: { mode: CompanyDeliveryFormMode }) {
  const navigate = useAdminNavigate();
  const params = useParams();

  return <DeliveryZonesPage formMode={mode} onNavigate={navigate} zoneId={params.zoneId} />;
}

function DeliveryZoneListRoute() {
  const navigate = useAdminNavigate();

  return <DeliveryZonesPage onNavigate={navigate} />;
}

function PriceImportsRoute() {
  const navigate = useAdminNavigate();

  return <PriceImportsPage onNavigate={navigate} />;
}

function PriceImportDetailRoute() {
  const { dictionaries } = useAdminContext();
  const navigate = useAdminNavigate();
  const params = useParams();
  const importId = requireRouteParam(params.importId, ADMIN_HOME_PATH);

  if (importId === null) {
    return <Navigate replace to={ADMIN_HOME_PATH} />;
  }

  return (
    <PriceImportDetailPage
      dictionaries={dictionaries}
      entityId={importId}
      onNavigate={navigate}
    />
  );
}

function useAdminNavigate(): (path: string) => void {
  const navigate = useNavigate();

  return (path: string): void => {
    navigate(path);
  };
}

function requireRouteParam(value: string | undefined, fallbackPath: string): string | null {
  if (value !== undefined && value !== '') {
    return value;
  }

  return null;
}
